package com.matchpredictor.web

import com.matchpredictor.web.api.ApiClient
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import kotlin.js.Promise

private val notificationsScope = MainScope()

fun settingsPage(): HTMLElement {
    val section = el("section", "page")
    section.appendChild(el("h1", text = "Настройки"))

    val account = el("div", "admin-block settings-account")
    account.appendChild(el("h2", text = "Аккаунт"))

    val accountStatus = el("p", "muted", "Загрузка…")
    account.appendChild(accountStatus)

    val formPanel = el("div", "mail-form-panel")
    val emailInput = input("email", "Email")
    emailInput.setAttribute("autocomplete", "email")
    val passwordInput = input("password", "Текущий пароль")
    passwordInput.setAttribute("autocomplete", "current-password")
    val accountError = el("p", "form-error")
    val accountActions = el("div", "actions")
    formPanel.appendChild(field("Email", emailInput))
    formPanel.appendChild(field("Текущий пароль (для смены email)", passwordInput))
    formPanel.appendChild(accountActions)
    formPanel.appendChild(accountError)
    account.appendChild(formPanel)

    val progressPanel = el("div", "mail-progress")
    progressPanel.hidden = true
    progressPanel.appendChild(el("div", "mail-spinner").also { it.setAttribute("aria-hidden", "true") })
    val progressText = el("p", "mail-progress-title", "Отправляем письмо…")
    val progressHint = el("p", "muted", "Обычно это занимает несколько секунд.")
    progressPanel.appendChild(progressText)
    progressPanel.appendChild(progressHint)
    account.appendChild(progressPanel)

    val successPanel = el("div", "mail-success")
    successPanel.hidden = true
    val successTitle = el("p", "mail-success-title", "Письмо отправлено")
    val successBody = el("div", "mail-success-body")
    successPanel.appendChild(successTitle)
    successPanel.appendChild(successBody)
    val successActions = el("div", "actions")
    successPanel.appendChild(successActions)
    account.appendChild(successPanel)

    section.appendChild(account)

    fun setBusy(busy: Boolean, label: String = "Отправляем письмо…") {
        progressPanel.hidden = !busy
        formPanel.hidden = busy
        successPanel.hidden = true
        if (busy) {
            progressText.textContent = label
            accountStatus.hidden = true
        } else {
            accountStatus.hidden = false
        }
        emailInput.disabled = busy
        passwordInput.disabled = busy
    }

    lateinit var refreshAccountUi: (UserDto) -> Unit

    fun showMailSuccess(email: String, added: Boolean) {
        progressPanel.hidden = true
        formPanel.hidden = true
        successPanel.hidden = false
        accountStatus.hidden = true
        successTitle.textContent = if (added) "Email сохранён" else "Письмо отправлено"
        successBody.innerHTML = ""
        successBody.appendChild(
            el(
                "p",
                text = "Мы отправили ссылку на подтверждение на $email. Перейдите по ней, чтобы подтвердить почту."
            )
        )
        successBody.appendChild(
            el(
                "p",
                "muted",
                "Если письма нет во «Входящих», проверьте папку «Спам» или «Промоакции»."
            )
        )
        successActions.innerHTML = ""
        successActions.appendChild(button("Понятно", "primary") {
            successPanel.hidden = true
            formPanel.hidden = false
            accountStatus.hidden = false
            AppState.user?.let { refreshAccountUi(it) }
        })
        successActions.appendChild(button("Отправить ещё раз", "ghost") {
            notificationsScope.launch {
                setBusy(true, "Отправляем письмо ещё раз…")
                accountError.textContent = ""
                runCatching {
                    ApiClient.resendVerification()
                    showMailSuccess(email, added = false)
                }.onFailure {
                    setBusy(false)
                    formPanel.hidden = false
                    accountStatus.hidden = false
                    accountError.textContent = errorMessage(it)
                }
            }
        })
    }

    refreshAccountUi = fun(user: UserDto) {
        if (!successPanel.hidden) return
        accountStatus.textContent = when {
            user.email.isNullOrBlank() -> "Email не указан — добавьте его, чтобы восстанавливать пароль."
            !user.emailVerified -> "Email ${user.email} ещё не подтверждён. Проверьте почту (и спам) или отправьте письмо снова."
            else -> "Email ${user.email} подтверждён."
        }
        if (!user.email.isNullOrBlank()) emailInput.value = user.email!!
        accountActions.innerHTML = ""
        if (!user.emailVerified && !user.email.isNullOrBlank()) {
            accountActions.appendChild(button("Отправить письмо ещё раз", "ghost") {
                notificationsScope.launch {
                    setBusy(true, "Отправляем письмо ещё раз…")
                    accountError.textContent = ""
                    runCatching {
                        ApiClient.resendVerification()
                        showMailSuccess(user.email!!, added = false)
                    }.onFailure {
                        setBusy(false)
                        accountError.textContent = errorMessage(it)
                    }
                }
            })
        }
        accountActions.appendChild(button(if (user.email.isNullOrBlank()) "Сохранить email" else "Сменить email", "primary") {
            if (emailInput.value.isBlank() || passwordInput.value.isBlank()) {
                accountError.textContent = "Укажите email и текущий пароль"
                return@button
            }
            val targetEmail = emailInput.value.trim()
            val wasBlank = user.email.isNullOrBlank()
            notificationsScope.launch {
                setBusy(true, if (wasBlank) "Сохраняем email и отправляем письмо…" else "Меняем email и отправляем письмо…")
                accountError.textContent = ""
                runCatching {
                    val updated = ApiClient.updateEmail(targetEmail, passwordInput.value)
                    AppState.user = updated
                    AppState.error = null
                    passwordInput.value = ""
                    showMailSuccess(updated.email ?: targetEmail, added = wasBlank)
                }.onFailure {
                    setBusy(false)
                    accountError.textContent = errorMessage(it)
                }
            }
        })
    }

    AppState.user?.let { refreshAccountUi(it) }
        ?: run {
            notificationsScope.launch {
                runCatching {
                    val me = ApiClient.me()
                    AppState.user = me
                    refreshAccountUi(me)
                }.onFailure {
                    accountStatus.textContent = errorMessage(it)
                }
            }
        }

    section.appendChild(el("h2", text = "Уведомления"))

    val toolbar = el("div", "notif-toolbar")
    val unreadLabel = el("span", "muted", "Непрочитанных: …")
    toolbar.appendChild(unreadLabel)
    toolbar.appendChild(button("Прочитать все", "ghost") {
        notificationsScope.launch {
            runCatching {
                ApiClient.markAllNotificationsRead()
                AppState.unreadNotifications = 0
                AppState.notice = "Все уведомления прочитаны"
                AppState.error = null
                render()
            }.onFailure {
                AppState.error = errorMessage(it)
                render()
            }
        }
    })

    val prefsBox = el("div", "admin-block notif-prefs")
    prefsBox.appendChild(el("h3", text = "Пуши"))
    val predToggle = document.createElement("input") as HTMLInputElement
    predToggle.type = "checkbox"
    predToggle.id = "pref-pred"
    val adminToggle = document.createElement("input") as HTMLInputElement
    adminToggle.type = "checkbox"
    adminToggle.id = "pref-admin"
    val inactivityToggle = document.createElement("input") as HTMLInputElement
    inactivityToggle.type = "checkbox"
    inactivityToggle.id = "pref-inactivity"
    val predRow = el("label", "notif-pref")
    predRow.appendChild(predToggle)
    predRow.appendChild(document.createTextNode(" Пуши о верных прогнозах"))
    val adminRow = el("label", "notif-pref")
    adminRow.appendChild(adminToggle)
    adminRow.appendChild(document.createTextNode(" Пуши админ-рассылок"))
    val inactivityRow = el("label", "notif-pref")
    inactivityRow.appendChild(inactivityToggle)
    inactivityRow.appendChild(document.createTextNode(" Напоминания, если долго не заходил"))
    val stickerPackToggle = document.createElement("input") as HTMLInputElement
    stickerPackToggle.type = "checkbox"
    stickerPackToggle.id = "pref-sticker-pack"
    val stickerPackRow = el("label", "notif-pref")
    stickerPackRow.appendChild(stickerPackToggle)
    stickerPackRow.appendChild(document.createTextNode(" Новые стикерпаки"))
    prefsBox.appendChild(predRow)
    prefsBox.appendChild(adminRow)
    prefsBox.appendChild(inactivityRow)
    prefsBox.appendChild(stickerPackRow)
    prefsBox.appendChild(button("Сохранить настройки пушей", "ghost") {
        notificationsScope.launch {
            runCatching {
                ApiClient.updateNotificationPreferences(
                    UpdateNotificationPreferencesRequest(
                        pushPredictionCorrect = predToggle.checked,
                        pushAdminBroadcast = adminToggle.checked,
                        pushInactivityReminder = inactivityToggle.checked,
                        pushStickerPack = stickerPackToggle.checked
                    )
                )
                AppState.notice = "Настройки сохранены"
                AppState.error = null
                render()
            }.onFailure {
                AppState.error = errorMessage(it)
                render()
            }
        }
    })

    section.appendChild(toolbar)
    section.appendChild(prefsBox)

    val list = el("div", "notif-list")
    list.appendChild(el("p", "muted", "Загрузка…"))
    section.appendChild(list)

    notificationsScope.launch {
        runCatching {
            val prefs = ApiClient.notificationPreferences()
            predToggle.checked = prefs.pushPredictionCorrect
            adminToggle.checked = prefs.pushAdminBroadcast
            inactivityToggle.checked = prefs.pushInactivityReminder
            stickerPackToggle.checked = prefs.pushStickerPack
            val unread = ApiClient.unreadNotificationCount().count
            AppState.unreadNotifications = unread
            unreadLabel.textContent = "Непрочитанных: $unread"
            val page = ApiClient.notifications()
            list.innerHTML = ""
            if (page.items.isEmpty()) {
                list.appendChild(el("p", "muted empty", "Пока нет уведомлений."))
            } else {
                page.items.forEach { item ->
                    list.appendChild(notificationCard(item))
                }
            }
        }.onFailure {
            list.innerHTML = ""
            list.appendChild(el("p", "form-error", errorMessage(it)))
        }
    }

    return section
}

private fun notificationCard(item: NotificationDto): HTMLElement {
    val unread = item.readAt == null
    val card = el("article", if (unread) "notif-item unread" else "notif-item")
    val head = el("div", "notif-head")
    head.appendChild(el("strong", text = item.title))
    head.appendChild(el("span", "muted", formatNotifTime(item.createdAt)))
    card.appendChild(head)
    card.appendChild(el("p", text = item.body))
    card.appendChild(
        el(
            "span",
            "badge " + when (item.type) {
                NotificationType.PREDICTION_CORRECT -> "settled"
                NotificationType.ADMIN_BROADCAST -> "open"
                NotificationType.INACTIVITY_REMINDER -> "open"
                NotificationType.STICKER_PACK -> "open"
            },
            when (item.type) {
                NotificationType.PREDICTION_CORRECT -> "прогноз"
                NotificationType.ADMIN_BROADCAST -> "рассылка"
                NotificationType.INACTIVITY_REMINDER -> "напоминание"
                NotificationType.STICKER_PACK -> "стикеры"
            }
        )
    )
    if (item.type == NotificationType.STICKER_PACK) {
        card.appendChild(button("В чат", "ghost") {
            AppState.page = Page.CHAT
            AppState.error = null
            AppState.notice = null
            render()
        })
    }
    if (unread) {
        card.appendChild(button("Прочитано", "ghost") {
            notificationsScope.launch {
                runCatching {
                    ApiClient.markNotificationRead(item.id)
                    AppState.unreadNotifications = (AppState.unreadNotifications - 1).coerceAtLeast(0)
                    render()
                }.onFailure {
                    AppState.error = errorMessage(it)
                    render()
                }
            }
        })
    }
    val pollId = item.payloadJson
        ?.let { Regex(""""pollId"\s*:\s*(\d+)""").find(it)?.groupValues?.get(1)?.toLongOrNull() }
    if (pollId != null) {
        card.appendChild(button("К линии", "ghost") {
            AppState.page = Page.POLLS
            AppState.error = null
            AppState.notice = null
            render()
        })
    }
    return card
}

private fun formatNotifTime(iso: String): String {
    return try {
        val d = kotlin.js.Date(iso)
        if (d.getTime().isNaN()) iso else d.toLocaleString("ru-RU")
    } catch (_: Throwable) {
        iso
    }
}

object WebPush {
    private const val AUTO_PROMPT_SESSION_KEY = "fg_push_auto_prompted"

    /** Best-effort: register if already granted, or ask once per session. Never throws to UI. */
    fun ensureEnabledInBackground() {
        notificationsScope.launch {
            runCatching { ensureEnabled() }
                .onFailure { console.info("Web Push auto-enable skipped:", it.message) }
        }
    }

    private suspend fun ensureEnabled() {
        val nav = kotlinx.browser.window.navigator.asDynamic()
        if (nav.serviceWorker == null || js("typeof PushManager === 'undefined'") as Boolean) {
            return
        }
        if (js("typeof Notification === 'undefined'") as Boolean) return

        val permission = js("Notification.permission") as String
        when (permission) {
            "denied" -> return
            "granted" -> enable()
            else -> {
                val already = kotlinx.browser.window.sessionStorage.getItem(AUTO_PROMPT_SESSION_KEY)
                if (already == "1") return
                kotlinx.browser.window.sessionStorage.setItem(AUTO_PROMPT_SESSION_KEY, "1")
                enable()
            }
        }
    }

    suspend fun enable() {
        val nav = kotlinx.browser.window.navigator.asDynamic()
        if (nav.serviceWorker == null || js("typeof PushManager === 'undefined'") as Boolean) {
            throw IllegalStateException("Браузер не поддерживает Web Push")
        }
        val publicKey = ApiClient.vapidPublicKey().publicKey
        val registration = (nav.serviceWorker.register("/sw.js") as Promise<dynamic>).await()
        val permission = requestPermission()
        if (permission != "granted") {
            throw IllegalStateException("Разрешение на уведомления не выдано")
        }
        val pushManager = registration.pushManager
        var subscription = (pushManager.getSubscription() as Promise<dynamic>).await()
        if (subscription == null) {
            val keyBytes = urlBase64ToUint8Array(publicKey)
            val options = js("{userVisibleOnly: true}")
            options.applicationServerKey = keyBytes
            subscription = (pushManager.subscribe(options) as Promise<dynamic>).await()
        }
        val endpoint = subscription.endpoint as String
        val p256dh = arrayBufferToBase64Url(subscription.getKey("p256dh"))
        val auth = arrayBufferToBase64Url(subscription.getKey("auth"))
        ApiClient.registerPush(
            PushRegisterRequest(
                platform = PushPlatform.WEB,
                provider = PushProvider.WEB_PUSH,
                endpoint = endpoint,
                p256dh = p256dh,
                auth = auth,
                appId = "web",
                appVersion = "1"
            )
        )
    }

    private suspend fun requestPermission(): String {
        val result = js("Notification.requestPermission()")
        return if (js("result && typeof result.then === 'function'") as Boolean) {
            (result as Promise<String>).await()
        } else {
            result as String
        }
    }

    private fun urlBase64ToUint8Array(base64String: String): dynamic {
        return js(
            """
            (function(b64) {
              var padding = '='.repeat((4 - b64.length % 4) % 4);
              var base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
              var rawData = window.atob(base64);
              var outputArray = new Uint8Array(rawData.length);
              for (var i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
              return outputArray;
            })
            """
        ).unsafeCast<(String) -> dynamic>()(base64String)
    }

    private fun arrayBufferToBase64Url(buffer: dynamic): String {
        return js(
            """
            (function(buffer) {
              var bytes = new Uint8Array(buffer);
              var str = '';
              for (var i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
              return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
            })
            """
        ).unsafeCast<(dynamic) -> String>()(buffer)
    }
}
